// ══════════════════════════════════════════════════════════════════════════════════════════════
//  B-2254 · LAS PÁGINAS DE UN PDF PINTADAS CON pdf.js, QUE SE AMPLÍAN SIN QUE LA LETRA SALGA BORROSA   (27-oct-2026)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  Un PDF dentro de la app (components/VisorDocumento) o en una vista previa del iPhone (utils/pdfEnLinea) se pinta
//  página a página en lienzos, y cada lienzo se estira al 100 % del ancho del contenido de la zona ampliable
//  (utils/zonaAmpliable.jsx). Al ampliar, el lienzo se ve más grande pero con los mismos píxeles: la letra pequeña,
//  justo la que se quiere leer, sale borrosa. Por eso, al soltar los dedos, las páginas que se ven (y las de al lado) se
//  vuelven a pintar con los píxeles que pide el zoom (con su tope: utils/pellizco.js, anchoDePintado), en un lienzo
//  nuevo que sustituye al viejo cuando ya está pintado (sin parpadeo). Las que quedan lejos vuelven a su tamaño de
//  partida, para que un informe de cuarenta páginas no se coma la memoria del teléfono.
//
//  Sin React y sin pdf.js dentro: quien llama trae la página (`pdfjs.getDocument(...).getPage(i)`) y el `document`.
// ══════════════════════════════════════════════════════════════════════════════════════════════
import { anchoDePintado } from './pellizco'

// Más de 2 píxeles por punto no se nota en un teléfono y cuadruplica la memoria.
export const DPR_MAX = 2
export const dprDe = (w) => Math.min(Math.max(1, Number(w && w.devicePixelRatio) || 1), DPR_MAX)

const rectDe = (el) => { try { return el && el.getBoundingClientRect ? el.getBoundingClientRect() : null } catch (_) { return null } }

// El aspecto de la página: el lienzo ocupa todo el ancho del contenido y su alto sale de su proporción.
// EL HUECO ENTRE PÁGINAS VA EN % (un margen en % se mide sobre el ANCHO): así crece con el zoom igual que las páginas, y el
// punto que hay entre los dedos se queda debajo de ellos también en la página 20. Con 10 px fijos, en la página 20 a ×2 el
// documento se iba casi 200 px de los dedos.
export const HUECO_ENTRE_PAGINAS = '1.6%'
export function vestirPagina(lienzo, i, { claro = false } = {}) {
  Object.assign(lienzo.style, {
    width: '100%', height: 'auto', display: 'block', margin: '0 0 ' + HUECO_ENTRE_PAGINAS, background: '#fff', borderRadius: '3px',
    boxShadow: claro ? '0 1px 6px rgba(0,0,0,0.18)' : '0 2px 12px rgba(0,0,0,0.35)',
  })
  if (typeof lienzo.setAttribute === 'function') lienzo.setAttribute('aria-label', 'Página ' + i)
}

// Pinta una página en un lienzo NUEVO de `anchoPx` píxeles de ancho. Devuelve el lienzo y la proporción (alto / ancho).
export async function pintarPagina(pagina, anchoPx, documento) {
  const d = documento || (typeof document !== 'undefined' ? document : null)
  const vp1 = pagina.getViewport({ scale: 1 })
  const vp = pagina.getViewport({ scale: Math.max(1, anchoPx) / (vp1.width || 1) })
  const lienzo = d.createElement('canvas')
  lienzo.width = Math.max(1, Math.floor(vp.width))
  lienzo.height = Math.max(1, Math.floor(vp.height))
  await pagina.render({ canvasContext: lienzo.getContext('2d'), viewport: vp }).promise
  return { lienzo, proporcion: (vp1.height || 1) / (vp1.width || 1) }
}

// Suelta la memoria de un lienzo: iOS no la devuelve hasta que el lienzo se queda a cero.
export function soltarLienzo(l) { try { if (l) { l.width = 0; l.height = 0 } } catch (_) { /* nada */ } }

// Vuelve a pintar las páginas con los píxeles que pide el zoom. `paginas` es la lista viva del visor:
// { pagina, lienzo, proporcion, pintado (ancho en píxeles del lienzo), i }. `sigue()` dice si este afinado sigue
// valiendo (si se ha vuelto a ampliar, o se ha cerrado, se para sin tocar nada más).
export async function afinarPaginas({ paginas, zona, zoom, anchoCss, dpr = 1, sigue = () => true, documento, claro = false }) {
  const rz = rectDe(zona)
  for (const p of (paginas || []).slice()) {
    if (!sigue()) return
    const r = rectDe(p.lienzo)
    // Las que se ven y las de al lado (una pantalla por arriba y otra por abajo), al zoom; las demás, a su tamaño.
    const cerca = !rz || !r || (r.bottom > rz.top - rz.height && r.top < rz.bottom + rz.height)
    const objetivo = anchoDePintado({ anchoCss, zoom: cerca ? zoom : 1, dpr, proporcion: p.proporcion })
    if (!p.pintado || Math.abs(objetivo - p.pintado) / p.pintado < 0.12) continue
    let nuevo = null
    try { nuevo = (await pintarPagina(p.pagina, objetivo, documento)).lienzo } catch (_) { continue }
    if (!sigue()) { soltarLienzo(nuevo); return }
    const padre = p.lienzo && p.lienzo.parentNode
    if (!padre || typeof padre.replaceChild !== 'function') { soltarLienzo(nuevo); continue }
    vestirPagina(nuevo, p.i, { claro })
    padre.replaceChild(nuevo, p.lienzo)
    soltarLienzo(p.lienzo)
    p.lienzo = nuevo
    p.pintado = objetivo
  }
}
